import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from '../../common_components/Toast/ToastUtils';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import ClientGroupHeader from '../../common_components/ClientGroupHeader';
import PagesCommonHeader from '../../common_components/PagesCommonHeader';
import LastUpdatedInfo from '../../common_components/LastUpdatedInfo';
import CustomDatePicker from '../../common_components/daterangepicker/CustomDatePicker';
import CustomMonthPicker from '../../common_components/daterangepicker/CustomMonthPicker';
import ChannelTabs from '../../common_components/ChannelTabs';
import { CalendarDate } from '@internationalized/date';
import { useDateRange } from '../../hooks';
import { useClientSelection } from '../../hooks/useClientSelection';
import { refreshAccountPerformance, selectRefreshLoading } from '../../redux/accountPerformanceSlice';
import { fetchAccountPerformanceClientReport, selectAccountPerformanceClientReportData, selectAccountPerformanceClientReportLoading, selectAccountPerformanceClientReportLastUpdated } from '../../redux/accountPerformanceClientReportSlice';
import { fetchAccountPerformanceTracker, selectTrackerLastUpdated } from '../../redux/accountPerformanceTrackerSlice';
import { setActiveModuleKey, clearActiveModuleKey } from '../../redux/userDataSlice';
import ClientReportTab from './tabs/ClientReportTab';
import PerformanceTrackerTab from './tabs/PerformanceTrackerTab';
import CommonLoader from '../../common_components/CommonLoader';
import { useAccountOverview } from '../../context/AccountOverviewContext';
const APR_SESSION_KEY = 'g8a_apr_state';
const readSession = () => {
    try {
        return JSON.parse(sessionStorage.getItem(APR_SESSION_KEY) || '{}');
    }
    catch {
        return {};
    }
};
const writeSession = (patch) => {
    try {
        sessionStorage.setItem(APR_SESSION_KEY, JSON.stringify({ ...readSession(), ...patch }));
    }
    catch { }
};
const AccountPerformanceReport = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    // Read persisted session once on mount — used only for initial state derivation
    const [session] = useState(() => readSession());
    // Session always wins over location.state:
    //   - page refresh  → location.state survives (browser keeps history) AND session has data → use session
    //   - fresh nav     → session was cleared on previous unmount, so it's empty → use location.state
    //   - direct URL    → both empty → defaults
    const initTab = session.activeTab || 'performance_tracker';
    const initDateRange = session.dateRange || null;
    const initCompare = session.compareDateRange || null;
    const initMonth = session.trackerMonth || location.state?.month || '';
    const { isArchiveMode, setIsArchiveMode } = useAccountOverview();
    const { selectedClientId, selectedGroupAccountId, selectedId, clientList, utilityLoading, clientOptions, groupAccountOptions, currentSelectedGroupAccountLabel, handleClientChange, handleGroupChange } = useClientSelection('account_performance', {
        status: isArchiveMode ? 'inactive' : 'active'
    });
    // ── Keep the Active/Archive toggle in sync with the selected client's actual status ──
    // Needed because isArchiveMode lives in a page-agnostic context and isn't reset when a
    // client is opened directly (e.g. via navigation state) rather than via this page's own toggle.
    const archiveSyncAttemptedRef = useRef(null);
    useEffect(() => {
        if (utilityLoading || !selectedClientId || clientList.length === 0)
            return;
        const currentClient = clientList.find((c) => c._id === selectedClientId);
        if (currentClient) {
            archiveSyncAttemptedRef.current = null;
            const shouldBeArchived = currentClient.status === 'inactive';
            if (shouldBeArchived !== isArchiveMode)
                setIsArchiveMode(shouldBeArchived);
            return;
        }
        // Client isn't in the current-status list — it likely belongs to the other status.
        // Only try flipping once per client to avoid oscillating on a genuinely missing client.
        if (archiveSyncAttemptedRef.current !== selectedClientId) {
            archiveSyncAttemptedRef.current = selectedClientId;
            setIsArchiveMode(!isArchiveMode);
        }
    }, [utilityLoading, clientList, selectedClientId, isArchiveMode, setIsArchiveMode]);
    const [activeTab, setActiveTab] = useState(initTab);
    const [refreshTriggeredClients, setRefreshTriggeredClients] = useState({});
    const isRefreshTriggeredForClient = refreshTriggeredClients[selectedId] || false;
    const [trackerMonth, setTrackerMonth] = useState(() => {
        if (initMonth) {
            const [year, month] = initMonth.split('-');
            if (year && month)
                return new CalendarDate(Number(year), Number(month), 1);
        }
        const d = new Date();
        return new CalendarDate(d.getFullYear(), d.getMonth() + 1, 1);
    });
    const reportData = useSelector(selectAccountPerformanceClientReportData);
    const reportLoading = useSelector(selectAccountPerformanceClientReportLoading);
    const clientReportLastUpdatedAt = useSelector(selectAccountPerformanceClientReportLastUpdated);
    const trackerLastUpdatedAt = useSelector(selectTrackerLastUpdated);
    const currentLastUpdatedAt = activeTab === 'performance_tracker' ? trackerLastUpdatedAt : clientReportLastUpdatedAt;
    const refreshLoading = useSelector(selectRefreshLoading);
    const { dateRange, compareDateRange, handleDateChange, isCompareOn, dateRangeKey, compareDateRangeKey } = useDateRange({
        defaultComparison: true,
        defaultCompareType: 'mom',
        initialMonth: initMonth,
        initialDateRange: initDateRange,
        initialCompareDateRange: initCompare,
    });
    const isFirstClientSync = useRef(true);
    // ── Persist tab / client / group ──────────────────────────────────────────
    useEffect(() => {
        writeSession({ clientId: selectedClientId, groupId: selectedGroupAccountId, activeTab });
        // Sync the initially resolved client to global storage (e.g., if arrived via URL param)
        if (isFirstClientSync.current && selectedClientId) {
            localStorage.setItem('globalSelectedClientId', selectedClientId);
            isFirstClientSync.current = false;
        }
    }, [selectedClientId, selectedGroupAccountId, activeTab]);
    // ── Persist date range whenever it changes ────────────────────────────────
    useEffect(() => {
        if (!dateRange?.start || !dateRange?.end)
            return;
        writeSession({
            dateRange: { start: dateRange.start.toString(), end: dateRange.end.toString() },
            compareDateRange: compareDateRange?.start
                ? { start: compareDateRange.start.toString(), end: compareDateRange.end.toString() }
                : null,
        });
    }, [dateRangeKey, compareDateRangeKey]);
    // ── Persist trackerMonth whenever it changes ─────────────────────────────
    useEffect(() => {
        if (!trackerMonth || trackerMonth === "ALL_TIME")
            return;
        const formattedMonth = `${trackerMonth.year}-${String(trackerMonth.month).padStart(2, '0')}`;
        writeSession({ trackerMonth: formattedMonth });
    }, [trackerMonth]);
    // ── Clear session on SPA navigation away, but NOT on page refresh ─────────
    useEffect(() => {
        let isPageRefresh = false;
        const markRefresh = () => { isPageRefresh = true; };
        window.addEventListener('beforeunload', markRefresh);
        return () => {
            window.removeEventListener('beforeunload', markRefresh);
            if (!isPageRefresh)
                sessionStorage.removeItem(APR_SESSION_KEY);
        };
    }, []);
    // ── Module key registration (required for x-module-key header on all API calls) ──
    useEffect(() => {
        dispatch(setActiveModuleKey('account_performance'));
        return () => { dispatch(clearActiveModuleKey()); };
    }, [dispatch]);
    useEffect(() => {
        if (activeTab === 'client_report' && selectedId && dateRange?.start && dateRange?.end) {
            const params = {
                client_id: selectedId,
                start_date: dateRange.start.toString(),
                end_date: dateRange.end.toString(),
                module_key: "account_performance",
            };
            if (isCompareOn && compareDateRange?.start && compareDateRange?.end) {
                params.compare_date_range = {
                    start_date: compareDateRange.start.toString(),
                    end_date: compareDateRange.end.toString(),
                };
            }
            dispatch(fetchAccountPerformanceClientReport(params));
        }
    }, [activeTab, selectedId, dateRangeKey, compareDateRangeKey, isCompareOn, dispatch]);
    useEffect(() => {
        if (activeTab === 'performance_tracker' && selectedId && trackerMonth && trackerMonth !== "ALL_TIME") {
            const formattedMonth = `${trackerMonth.year}-${String(trackerMonth.month).padStart(2, '0')}`;
            dispatch(fetchAccountPerformanceTracker({
                client_id: selectedId,
                month: formattedMonth
            }));
        }
    }, [activeTab, selectedId, trackerMonth, dispatch]);
    const handleRefresh = async () => {
        if (!selectedId || refreshLoading || isRefreshTriggeredForClient)
            return;
        // Performance Tracker: trigger network data refresh then re-fetch tracker data
        if (activeTab === 'performance_tracker') {
            if (!trackerMonth || trackerMonth === "ALL_TIME")
                return;
            const month = `${trackerMonth.year}-${String(trackerMonth.month).padStart(2, '0')}`;
            toast.success("Refreshing network data. Please wait...");
            const result = await dispatch(refreshAccountPerformance({ month, clientId: selectedId, moduleKey: "account_performance", submodule: "performance_tracker" }));
            if (refreshAccountPerformance.fulfilled.match(result)) {
                const failedNetworks = result.payload?.failed_networks || [];
                if (failedNetworks.length > 0) {
                    const failedNames = [...new Map(failedNetworks.map((n) => [n.network, n.error])).entries()]
                        .map(([name, err]) => {
                        let eStr = typeof err === 'string' ? err : err?.message;
                        if (eStr && eStr.toLowerCase().includes('time'))
                            eStr = 'Timeout';
                        return `${name.charAt(0).toUpperCase() + name.slice(1)}${eStr ? ` (${eStr})` : ''}`;
                    })
                        .join(', ');
                    toast.warning(`Performance Tracker refresh completed.\nFailed to refresh: ${failedNames}.`);
                }
                else {
                    toast.success("Performance Tracker refresh completed successfully.");
                }
                dispatch(fetchAccountPerformanceTracker({ client_id: selectedId, month }));
            }
            return;
        }
        // Client Report: refresh networks then re-fetch report data
        if (!dateRange?.start || !dateRange?.end)
            return;
        const startDate = dateRange.start.toString();
        const endDate = dateRange.end.toString();
        toast.success("Refreshing network data. Please wait...");
        const result = await dispatch(refreshAccountPerformance({ startDate, endDate, clientId: selectedId }));
        if (refreshAccountPerformance.fulfilled.match(result)) {
            const failedNetworks = result.payload?.failed_networks || [];
            if (failedNetworks.length > 0) {
                const failedNames = [...new Map(failedNetworks.map((n) => [n.network, n.error])).entries()]
                    .map(([name, err]) => {
                    let eStr = typeof err === 'string' ? err : err?.message;
                    if (eStr && eStr.toLowerCase().includes('time'))
                        eStr = 'Timeout';
                    return `${name.charAt(0).toUpperCase() + name.slice(1)}${eStr ? ` (${eStr})` : ''}`;
                })
                    .join(', ');
                toast.warning(`Client Report refresh completed.\nFailed to refresh: ${failedNames}.`);
            }
            else {
                toast.success("Client Report refresh completed successfully.");
            }
            const params = {
                client_id: selectedId,
                start_date: dateRange.start.toString(),
                end_date: dateRange.end.toString(),
                module_key: "account_performance",
            };
            if (isCompareOn && compareDateRange?.start && compareDateRange?.end) {
                params.compare_date_range = {
                    start_date: compareDateRange.start.toString(),
                    end_date: compareDateRange.end.toString(),
                };
            }
            dispatch(fetchAccountPerformanceClientReport(params));
        }
    };
    // ── Render ────────────────────────────────────────────────────────────────
    return (_jsx(Box, { sx: { p: 1, height: '100%', display: 'flex', gap: 1, flexDirection: 'column', position: 'relative' }, children: refreshLoading ? (_jsx(Box, { sx: {
                position: "absolute",
                inset: 0,
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
            }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : (_jsxs(_Fragment, { children: [_jsx(ClientGroupHeader, { selectedClientId: selectedClientId, clientOptions: clientOptions, handleClientChange: handleClientChange, selectedGroupAccountId: selectedGroupAccountId, groupAccountOptions: groupAccountOptions, handleGroupChange: handleGroupChange, currentSelectedGroupAccountLabel: currentSelectedGroupAccountLabel, isRefreshing: reportLoading }), _jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: _jsx(LastUpdatedInfo, { updatedAt: currentLastUpdatedAt, moduleName: "account_performance", onRefresh: handleRefresh, loading: refreshLoading, disabled: refreshLoading || isRefreshTriggeredForClient, disabledReason: isRefreshTriggeredForClient
                            ? "Refresh already in progress. You'll receive an email when complete."
                            : undefined }), rightSection: _jsx(Box, { sx: { display: "flex", alignItems: "center", gap: 2 }, children: activeTab === "performance_tracker" ? (_jsx(CustomMonthPicker, { value: trackerMonth, onChange: setTrackerMonth, placeholder: "Select Month", showAllTime: false })) : (_jsx(CustomDatePicker, { value: dateRange, compareValue: compareDateRange, onApply: handleDateChange, comparison: true, placeholder: "Select Date Range", comparePlaceholder: "Compare Range", disabled: reportLoading })) }) }), _jsx(Box, { sx: {
                        bgcolor: "#fff",
                        borderRadius: "8px",
                        border: "1px solid #E5E7EB",
                    }, children: _jsx(ChannelTabs, { value: activeTab, onChange: setActiveTab, tabs: [
                            { label: "Client Report", key: "client_report" },
                            { label: "Performance Tracker", key: "performance_tracker" },
                        ] }) }), _jsx(Box, { sx: { flex: 1, bgcolor: "#FAFAFA" }, children: reportLoading ? (_jsx(Box, { sx: { display: "flex", justifyContent: "center", p: 4 }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : (_jsxs(_Fragment, { children: [activeTab === "client_report" && (_jsx(ClientReportTab, { reportData: reportData, clientId: selectedId })), activeTab === "performance_tracker" && (_jsx(PerformanceTrackerTab, {}))] })) })] })) }));
};
export default AccountPerformanceReport;
